import { useState, useEffect, useCallback } from "react";
import { Check, X, ArrowRight, Loader2, Star, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { eur: "€", usd: "$" };

export const PricingSection = ({ showBooking = false }) => {
  const { t, language } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [showForm, setShowForm] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/plans?lang=${language}`);
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [language, t]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleCheckout = async (planId) => {
    if (!email || !name) {
      toast.error(`${t("pricing.nameLabel")} + ${t("pricing.emailLabel")}`);
      return;
    }

    setCheckoutLoading(planId);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        plan_id: planId,
        email: email,
        name: name,
        preferred_name: preferredName || null,
        language: language,
        origin_url: window.location.origin,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(t("common.error"));
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="py-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2c4c3b]" />
      </div>
    );
  }

  const basePlan = plans.find((p) => p.id === "base");
  const premiumPlan = plans.find((p) => p.id === "premium");
  const luxuryPlan = plans.find((p) => p.id === "luxury");

  const planOrder = [basePlan, premiumPlan, luxuryPlan].filter(Boolean);

  const getPlanStyle = (planId) => {
    switch (planId) {
      case "base":
        return {
          container: "bg-white border-stone-200",
          label: t("pricing.base"),
          labelClass: "text-[#8a9a86]",
          priceClass: "text-[#2c4c3b]",
          buttonVariant: "outline",
          buttonClass: "border-[#2c4c3b]/20 text-[#2c4c3b] hover:bg-[#2c4c3b]/5",
        };
      case "premium":
        return {
          container: "bg-[#e8ece6] border-[#8a9a86]/30",
          label: t("pricing.recommended"),
          labelClass: "text-[#2c4c3b]",
          priceClass: "text-[#2c4c3b]",
          buttonVariant: "default",
          buttonClass: "bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50",
          badge: true,
        };
      case "luxury":
        return {
          container: "bg-[#2c4c3b] border-[#2c4c3b]",
          label: t("pricing.exclusive"),
          labelClass: "text-[#d4c4a8]",
          priceClass: "text-stone-100",
          textClass: "text-stone-100",
          subtextClass: "text-stone-300",
          buttonVariant: "default",
          buttonClass: "bg-stone-100 hover:bg-stone-200 text-[#2c4c3b]",
          dark: true,
        };
      default:
        return {};
    }
  };

  const inputStyle = (isDark) =>
    `w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 text-sm ${
      isDark
        ? "border-stone-600 bg-[#1a2e22] text-stone-100 placeholder-stone-400 focus:ring-[#d4c4a8]/50"
        : "border-stone-200 focus:ring-[#2c4c3b]/20"
    }`;

  return (
    <section
      id="piani"
      className="py-24 md:py-32 bg-stone-50"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs tracking-[0.2em] uppercase text-[#8a9a86] font-medium">
            {t("pricing.label")}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 tracking-tight leading-tight mt-4 mb-6">
            {t("pricing.title")}
          </h2>
          <p className="text-stone-600 leading-relaxed">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {planOrder.map((plan) => {
            const style = getPlanStyle(plan.id);
            const isDark = style.dark;
            const symbol = CURRENCY_SYMBOLS[plan.currency] || "€";

            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-8 border shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 ${style.container}`}
                data-testid={`plan-${plan.id}`}
              >
                {/* Badge */}
                {style.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#2c4c3b] rounded-full">
                    <span className="text-xs text-stone-100 font-medium">
                      {t("pricing.popular")}
                    </span>
                  </div>
                )}

                {plan.id === "luxury" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#d4c4a8] rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 text-[#2c4c3b]" fill="#2c4c3b" />
                    <span className="text-xs text-[#2c4c3b] font-medium">
                      {t("pricing.vip")}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <span className={`text-xs tracking-[0.15em] uppercase font-medium ${style.labelClass}`}>
                    {style.label}
                  </span>
                  <h3 className={`font-serif text-2xl mt-2 ${isDark ? "text-stone-100" : "text-stone-900"}`}>
                    {plan.name}
                  </h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className={`font-serif text-4xl ${style.priceClass}`} data-testid={`plan-${plan.id}-price`}>
                      {symbol}{plan.price}
                    </span>
                    <span className={`text-sm ${isDark ? "text-stone-400" : "text-stone-500"}`}>
                      {t("pricing.month")}
                    </span>
                  </div>
                  <p className={`mt-4 text-sm leading-relaxed ${isDark ? "text-stone-300" : "text-stone-600"}`}>
                    {plan.description}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => {
                    const isHighlight = plan.highlight && feature === plan.highlight;
                    return (
                      <li key={`${plan.id}-feature-${feature}`} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isDark ? "bg-[#8a9a86]/30" : "bg-[#e8ece6]"
                        }`}>
                          {isHighlight ? (
                            <Star className={`w-3 h-3 ${isDark ? "text-[#d4c4a8]" : "text-[#2c4c3b]"}`} fill="currentColor" />
                          ) : (
                            <Check className={`w-3 h-3 ${isDark ? "text-stone-100" : "text-[#2c4c3b]"}`} />
                          )}
                        </div>
                        <span className={`text-sm ${isHighlight ? "font-medium" : ""} ${isDark ? "text-stone-200" : "text-stone-600"}`}>
                          {feature}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* Excluded features */}
                {plan.excluded && plan.excluded.length > 0 && (
                  <ul className="space-y-3 mb-8 pt-4 border-t border-dashed border-stone-200/30">
                    {plan.excluded.map((feature) => (
                      <li key={`${plan.id}-excluded-${feature}`} className="flex items-start gap-3 opacity-50">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isDark ? "bg-stone-700" : "bg-stone-100"
                        }`}>
                          <X className={`w-3 h-3 ${isDark ? "text-stone-500" : "text-stone-400"}`} />
                        </div>
                        <span className={`text-sm line-through ${isDark ? "text-stone-500" : "text-stone-400"}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* CTA */}
                {showForm === plan.id ? (
                  <div className="space-y-4 mt-auto">
                    <input
                      type="text"
                      placeholder={t("pricing.nameLabel")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputStyle(isDark)}
                      data-testid={`${plan.id}-name-input`}
                    />
                    <input
                      type="text"
                      placeholder={t("pricing.preferredNamePlaceholder")}
                      value={preferredName}
                      onChange={(e) => setPreferredName(e.target.value)}
                      className={inputStyle(isDark)}
                      data-testid={`${plan.id}-preferred-name-input`}
                    />
                    <p className={`text-xs -mt-2 ${isDark ? "text-stone-400" : "text-stone-500"}`}>
                      {t("pricing.preferredNameLabel")}
                    </p>
                    <input
                      type="email"
                      placeholder={t("pricing.emailLabel")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputStyle(isDark)}
                      data-testid={`${plan.id}-email-input`}
                    />
                    <Button
                      onClick={() => handleCheckout(plan.id)}
                      disabled={checkoutLoading === plan.id}
                      className={`w-full rounded-full py-6 ${style.buttonClass}`}
                      data-testid={`${plan.id}-checkout-btn`}
                    >
                      {checkoutLoading === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t("pricing.proceed")
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowForm(plan.id)}
                    className={`w-full rounded-full py-6 mt-auto group ${style.buttonClass}`}
                    variant={style.buttonVariant}
                    data-testid={`${plan.id}-subscribe-btn`}
                  >
                    {plan.id === "luxury" ? t("pricing.becomeVip") : t("pricing.subscribe")}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                )}

                {/* Booking link for Luxury plan */}
                {plan.id === "luxury" && !showForm && (
                  <Link to="/booking" className="block mt-3">
                    <Button
                      variant="outline"
                      className="w-full rounded-full py-5 border-[#d4c4a8] text-[#d4c4a8] hover:bg-[#d4c4a8]/10 group"
                      data-testid="luxury-booking-link"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {t("pricing.bookConsultation")}
                    </Button>
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Note */}
        <p className="text-center text-stone-500 text-sm mt-12">
          {t("pricing.note")}
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
